/* eslint-disable react/no-array-index-key */
import '@fontsource/poppins';
import { Icon } from '@iconify/react';
import axios from 'axios';
import dayjs from 'dayjs';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import React, { useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAuth } from '@src/contexts/authContext';

const url = `${process.env.REACT_APP_API_URL}`;

export default function UpdatePastSlot() {
  // const { clinicId } = useParams();
  const navigate = useNavigate();
  const state = useLocation();
  const { auth } = useAuth();
  const [open, setOpen] = useState(false);
  const handleBack = () => {
    navigate(state?.prevLocation || -1);
  };

  const { jobsId } = useParams();
  const [jobs, setJobs] = useState({
    price: '',
    scope: '',
    job_description: '',
    date: '',
    work_time_start: '',
    work_time_finish: '',
    break: [
      {
        start: '',
        finish: '',
      },
    ],
  });

  const [updateSlot, setUpdateSlot] = useState({
    breaks: [{ start: '', finish: '' }],
    price: '',
    scope: '',
    job_description: '',
    work_time_start: '',
    work_time_finish: '',
  });

  React.useEffect(() => {
    if (auth?.idToken) {
      axios
        .get(`${url}/jobs/${jobsId}`, {
          headers: {
            Authorization: `Bearer ${auth.idToken}`,
          },
        })
        .then((response) => {
          setJobs(response.data);
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
        });
    }
  }, [auth, jobsId]);

  React.useEffect(() => {
    if (jobs?.date && jobs?.work_time_start && jobs?.work_time_finish) {
      const parsedDate = dayjs(jobs.date, 'DD MMMM YYYY');

      const formattedWorkTimeStart = parsedDate
        .set('hour', dayjs(jobs.work_time_start, 'h:mm A').hour())
        .set('minute', dayjs(jobs.work_time_start, 'h:mm A').minute())
        .format('YYYY-MM-DDTHH:mm');

      const formattedWorkTimeFinish = parsedDate
        .set('hour', dayjs(jobs.work_time_finish, 'h:mm A').hour())
        .set('minute', dayjs(jobs.work_time_finish, 'h:mm A').minute())
        .format('YYYY-MM-DDTHH:mm');

      const formattedBreaks =
        jobs.break?.map((b) => {
          const jobDate = dayjs(jobs.date);
          const startTime = dayjs(b.start, 'h:mm A', true);
          const finishTime = dayjs(b.finish, 'h:mm A', true);

          return {
            break_start:
              jobDate.isValid() && startTime.isValid()
                ? jobDate
                    .set('hour', startTime.hour())
                    .set('minute', startTime.minute())
                    .format('YYYY-MM-DDTHH:mm')
                : '',
            break_finish:
              jobDate.isValid() && finishTime.isValid()
                ? jobDate
                    .set('hour', finishTime.hour())
                    .set('minute', finishTime.minute())
                    .format('YYYY-MM-DDTHH:mm')
                : '',
          };
        }) || [];

      setUpdateSlot({
        breaks: formattedBreaks || '',
        price: jobs.price || '',
        scope: jobs.scope || '',
        job_description: jobs.job_description || '',
        date: jobs?.date || '',
        work_time_start: formattedWorkTimeStart || '',
        work_time_finish: formattedWorkTimeFinish || '',
      });
    }
  }, [jobs]);

  function updatePastSlot() {
    const formData = {
      price: updateSlot.price || '',
      scope: updateSlot.scope || '',
      job_description: updateSlot.job_description || '',
      work_time_start: updateSlot.work_time_start || '',
      work_time_finish: updateSlot.work_time_finish || '',
      break: updateSlot?.breaks.map((b) => ({
        break_start: b.break_start,
        break_finish: b.break_finish,
      })),
    };

    axios
      .put(`${url}/admin/jobs/update-completed-job/${jobsId}`, formData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.idToken}`,
        },
      })
      .then((response) => {
        console.log('Slot updated successfully:', response.data);
        setUpdateSlot({ ...updateSlot });
        setOpen(true);
      })
      .catch((error) => {
        console.log('Error', error.message);
        toast.error(`Error: ${error.response.data.message}`, {
          autoClose: 2000,
          position: 'top-center',
        });
      });
    // console.log(formData);
  }

  // const handleOpenModal = () => {
  //   setOpen(false);
  // };

  const handleCloseModal = () => {
    setOpen(false);
    navigate(state?.prevLocation || -1);
  };

  const handleStartTimeChange = (newDate) => {
    if (newDate && newDate.isValid()) {
      setUpdateSlot({
        ...updateSlot,
        work_time_start: newDate.format('YYYY-MM-DDTHH:mm'),
      });
    } else {
      setUpdateSlot({
        ...updateSlot,
        work_time_start: '',
      });
    }
  };

  const handleEndTimeChange = (newDate) => {
    if (newDate && newDate.isValid()) {
      setUpdateSlot({
        ...updateSlot,
        work_time_finish: newDate.format('YYYY-MM-DDTHH:mm'),
      });
    } else {
      setUpdateSlot({
        ...updateSlot,
        work_time_finish: '',
      });
    }
  };

  const handlePriceChange = (e) => {
    const { value } = e.target;

    if (/^\d*$/.test(value)) {
      setUpdateSlot((prevState) => ({
        ...prevState,
        price: value,
      }));
    }
  };

  const handleScopeChange = (e) => {
    const { value } = e.target;
    setUpdateSlot((prevState) => ({
      ...prevState,
      scope: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleDescriptionChange = (e) => {
    setUpdateSlot((prevState) => ({
      ...prevState,
      job_description: e.target.value,
    }));
  };

  return (
    <Box sx={{ width: '100%', fontFamily: 'Poppins' }}>
      <Button
        sx={{
          textTransform: 'none',
          paddingLeft: 0,
        }}
        className='py-6 px-6 font-bold text-purple-500'
        variant='text'
        // eslint-disable-next-line react/jsx-no-bind
        onClick={handleBack}
      >
        Back
      </Button>
      <Box
        sx={{
          width: '100%',
          fontFamily: 'Poppins',
          display: 'flex',
          justifyContent: 'center',
          py: 2,
        }}
      >
        <Box
          sx={{
            width: '50%',
            backgroundColor: 'white',
            borderRadius: 4,
            boxShadow: '0px 4px 20px rgba(0,0,0,0.05)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              py: 3,
              px: 4,
              borderBottom: '1px solid #eee',
              background: 'linear-gradient(90deg, #06b6d4 0%, #0891b2 100%)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: 18,
              letterSpacing: 0.5,
            }}
          >
            Slot Information
          </Box>

          <Box sx={{ px: 4, py: 3 }}>
            {/* Working Hours */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: 'gray.700',
                  fontSize: 15,
                  mb: 1,
                }}
              >
                Working Hours
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                  <DateTimePicker
                    label='Time Start'
                    sx={{ width: '100%' }}
                    format='DD MMM YYYY - hh:mm A'
                    value={
                      jobs?.date && jobs?.work_time_start
                        ? dayjs(`${jobs.date} ${jobs.work_time_start}`)
                        : null
                    }
                    onChange={handleStartTimeChange}
                  />
                  <DateTimePicker
                    label='Time End'
                    sx={{ width: '100%' }}
                    format='DD MMM YYYY - hh:mm A'
                    value={
                      jobs?.date && jobs?.work_time_finish
                        ? dayjs(`${jobs.date} ${jobs.work_time_finish}`)
                        : null
                    }
                    onChange={handleEndTimeChange}
                  />
                </Box>
              </LocalizationProvider>
            </Box>

            {/* Break Time */}
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: 'gray.700',
                  fontSize: 15,
                  mb: 1,
                }}
              >
                Break Time (optional)
              </Typography>
              {updateSlot.breaks.map((b, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    mb: 2,
                    pt: 2,
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      label='Time Start'
                      sx={{ flex: 1 }}
                      format='DD MMM YYYY - hh:mm A'
                      value={b.break_start ? dayjs(b.break_start) : null}
                      onChange={(newDate) =>
                        setUpdateSlot((prev) => {
                          const updatedBreaks = [...prev.breaks];
                          updatedBreaks[idx].break_start =
                            newDate.format('YYYY-MM-DDTHH:mm');
                          return { ...prev, breaks: updatedBreaks };
                        })
                      }
                    />
                    <DateTimePicker
                      label='Time End'
                      sx={{ flex: 1 }}
                      format='DD MMM YYYY - hh:mm A'
                      value={b.break_finish ? dayjs(b.break_finish) : null}
                      onChange={(newDate) =>
                        setUpdateSlot((prev) => {
                          const updatedBreaks = [...prev.breaks];
                          updatedBreaks[idx].break_finish =
                            newDate.format('YYYY-MM-DDTHH:mm');
                          return { ...prev, breaks: updatedBreaks };
                        })
                      }
                    />
                    <Icon
                      icon='lucide:trash-2'
                      color='#ef4444'
                      width='24'
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setUpdateSlot((prevSlot) => ({
                          ...prevSlot,
                          breaks: prevSlot.breaks.filter(
                            (_, index) => index !== idx,
                          ),
                        }));
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Icon
                  className='pl-2'
                  icon='material-symbols:add-circle-outline-rounded'
                  color='#06b6d4'
                  width='33'
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    setUpdateSlot((prevSlot) => ({
                      ...prevSlot,
                      breaks: [
                        ...prevSlot.breaks,
                        { break_start: '', break_finish: '' },
                      ],
                    }))
                  }
                />
              </Box>
            </Box>

            {/* Hourly Rate */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: 'gray.700',
                  fontSize: 15,
                  mb: 1,
                }}
              >
                Hourly Rate
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  id='price'
                  type='number'
                  placeholder='Enter price'
                  className='font-poppins text-md rounded-md border border-gray-300 px-3 py-2 '
                  value={updateSlot?.price || ''}
                  onChange={handlePriceChange}
                  style={{ width: '205px', height: '50px' }}
                />

                <Typography sx={{ fontWeight: 'medium', color: 'gray.600' }}>
                  SGD
                </Typography>
              </Box>
            </Box>

            {/* Scope */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: 'gray.700',
                  fontSize: 15,
                  mb: 1,
                }}
              >
                Scope
              </Typography>
              <FormControl fullWidth>
                <Select
                  id='scope'
                  multiple
                  value={updateSlot?.scope || jobs?.scope || []}
                  onChange={handleScopeChange}
                  renderValue={(selected) =>
                    Array.isArray(selected) ? selected.join(', ') : ''
                  }
                  sx={{
                    borderRadius: 2,
                    fontSize: 14,
                    height: 50,
                  }}
                >
                  <MenuItem disabled value=''>
                    <em style={{ color: '#8C8C8C' }}>Select</em>
                  </MenuItem>
                  {[
                    'GP',
                    'Aesthetics',
                    'CHAS',
                    'PHPC',
                    'Covid Vaccines',
                    'DWD',
                    '2nd Doctor',
                    'Low Load',
                    'Moderate Load',
                    'Heavy Load',
                    'Specialist Clinic',
                  ].map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Job Description */}
            <Box sx={{ mb: 5 }}>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: 'gray.700',
                  fontSize: 15,
                  mb: 1,
                }}
              >
                Job Description
              </Typography>
              <textarea
                id='desc'
                rows='4'
                placeholder='Enter description'
                className='font-poppins text-md w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-cyan-500 focus:ring-cyan-500'
                value={updateSlot?.job_description || ''}
                onChange={handleDescriptionChange}
              />
            </Box>

            {/* Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant='contained'
                sx={{
                  textTransform: 'none',
                  width: 200,
                  height: 48,

                  fontWeight: 'bold',
                  fontSize: 15,
                  borderRadius: 2,
                }}
                className='text-md rounded-md bg-cyan-600 font-bold text-white'
                onClick={() => updatePastSlot()}
              >
                Update
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={open} onClose={handleCloseModal}>
        <DialogTitle>Save Successful</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The past slot has been updated successfully.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
